import { AlertTriangle, Play } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { useArmDelay } from "../../hooks/useArmDelay";
import { useBatchErrorMessage } from "../../hooks/useBatchErrorMessage";
import { useResumeBatch } from "../../hooks/useResumeBatch";
import type { PayoutBatchDetail } from "../../types/payoutBatches.types";
import { runnableRows } from "../../utils/batchStatus";
import { ConsequenceList, ConsequenceRow } from "./ConsequenceList";

export interface ResumeBatchDialogProps {
  batch: PayoutBatchDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applyBatch: (next: PayoutBatchDetail) => void;
}

/** Re-queues the rows a failed run never processed; paid rows are never repeated. Confirmed with a TOTP. */
export function ResumeBatchDialog({ batch, open, onOpenChange, applyBatch }: ResumeBatchDialogProps) {
  const { t } = useTranslations("payoutBatches");
  const describeError = useBatchErrorMessage();
  const resume = useResumeBatch(batch.id, applyBatch);
  const { armed, disarm } = useArmDelay(open);
  const [reviewed, setReviewed] = useState(false);
  const pending = runnableRows(batch.counters);

  const close = () => {
    setReviewed(false);
    disarm();
    resume.reset();
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && resume.isResuming) return;
    if (!next) close();
    else onOpenChange(true);
  };

  const confirm = async () => {
    const accepted = await resume.resume();
    if (accepted) {
      colocaToast.success(t("resume_success_toast"));
      close();
    }
  };

  return (
    <>
      <ColocaDialog
        open={open}
        onOpenChange={handleOpenChange}
        size="default"
        title={t("resume_title")}
        description={t("resume_description")}
        showCloseButton={!resume.isResuming}
        footer={
          <>
            <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={resume.isResuming} />}>
              {t("action_cancel")}
            </ColocaDialogClose>
            <ColocaButton
              variant="primary"
              icon={Play}
              loading={resume.isResuming}
              disabled={!armed || !reviewed || resume.isResuming}
              onClick={() => void confirm()}
            >
              {armed ? t("resume_confirm") : t("resume_arming")}
            </ColocaButton>
          </>
        }
      >
        <div className="space-y-4">
          <ConsequenceList>
            <ConsequenceRow
              label={t("resume_rows", { count: pending })}
              emphasis
              value={<span className="font-mono text-xl font-semibold tabular-nums">{pending}</span>}
            />
            <ConsequenceRow
              label={t("counters_paid")}
              value={<span className="font-mono tabular-nums">{batch.counters.paid}</span>}
            />
            <ConsequenceRow
              label={t("counters_failed")}
              value={<span className="font-mono tabular-nums">{batch.counters.failed}</span>}
            />
          </ConsequenceList>
          {resume.error && (
            <ColocaAlert variant="destructive" icon={AlertTriangle} text={describeError(resume.error)} />
          )}
          <ColocaCheckbox
            checked={reviewed}
            onCheckedChange={setReviewed}
            disabled={resume.isResuming}
            label={t("resume_reviewed_label")}
          />
        </div>
      </ColocaDialog>
      <TwoFactorActionModals
        controller={resume.twoFactor}
        requiredDescription={t("resume_two_factor_required")}
      />
    </>
  );
}
